import {
  clearStoredSessionProfile,
  SESSION_PROFILE_KEY,
  SESSION_USER_ID_KEY
} from "@/utils/sessionProfileClear.mjs";
import { invalidatePageReads } from "@/utils/pageReadScope.mjs";
import { clearRootNavigation, setRootNavigationIdentity } from "@/utils/rootNavigationState.mjs";

let cryptoKeyPromise;
let memoryProfile = null;
let profileRevision = 0;

function getSessionStorage() {
  try { return globalThis.sessionStorage } catch { return null; }
}

function storeProfileValue(key, value) {
  try {
    const storage = getSessionStorage();
    if (value == null) storage?.removeItem(key);
    else storage?.setItem(key, value);
  } catch { /* 隐私模式禁用存储时，会话仍由接口和内存资料管理。 */ }
}

function getSubtleCrypto() {
  return typeof window !== "undefined" && window.crypto?.subtle
    ? window.crypto.subtle
    : null;
}

function getCryptoKey() {
  const subtle = getSubtleCrypto();
  if (!subtle) return null;
  if (!cryptoKeyPromise) {
    cryptoKeyPromise = subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );
  }
  return cryptoKeyPromise;
}

function toBase64(bytes) {
  return btoa(String.fromCharCode(...bytes));
}

function fromBase64(value) {
  return Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
}

function pickProfile(profile) {
  return {
    userId: profile.userId,
    nickName: profile.nickName,
    companyIdFk: profile.companyIdFk,
    companyName: profile.companyName,
    deptName: profile.deptName,
    isDeptAdmin: profile.isDeptAdmin,
    isCompanyAdmin: profile.isCompanyAdmin,
    isSuperAdmin: profile.isSuperAdmin,
    mustChangePassword: Boolean(profile.mustChangePassword),
    recoverySession: Boolean(profile.recoverySession)
  };
}

export async function saveSessionProfile(profile) {
  const revision = ++profileRevision;
  const normalizedProfile = pickProfile(profile);
  if (typeof window !== "undefined") setRootNavigationIdentity(normalizedProfile, window);
  if (memoryProfile && (memoryProfile.userId !== normalizedProfile.userId
      || memoryProfile.companyIdFk !== normalizedProfile.companyIdFk)) {
    invalidatePageReads();
    if (typeof window !== "undefined") window.dispatchEvent(new Event("auth-profile-changed"));
  }
  memoryProfile = normalizedProfile;
  storeProfileValue(SESSION_USER_ID_KEY, profile.userId || '');

  const subtle = getSubtleCrypto();
  if (!subtle) {
    // 普通 HTTP IP 不提供 Web Crypto；只保留当前页面内存副本，避免明文写入 Web Storage。
    storeProfileValue(SESSION_PROFILE_KEY, null);
    return;
  }

  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const content = new TextEncoder().encode(JSON.stringify(normalizedProfile));
  const cipherText = await subtle.encrypt({ name: "AES-GCM", iv }, await getCryptoKey(), content);
  if (revision !== profileRevision) return;
  storeProfileValue(SESSION_PROFILE_KEY, JSON.stringify({
    iv: toBase64(iv),
    data: toBase64(new Uint8Array(cipherText))
  }));
}

export function getSessionUserId() {
  if (memoryProfile?.userId) return memoryProfile.userId;
  try { return getSessionStorage()?.getItem(SESSION_USER_ID_KEY) || null; } catch { return null; }
}

export async function getSessionProfile() {
  if (memoryProfile) {
    return { ...memoryProfile };
  }

  let cached;
  try { cached = getSessionStorage()?.getItem(SESSION_PROFILE_KEY); } catch { return null; }
  if (!cached) {
    return null;
  }

  const subtle = getSubtleCrypto();
  if (!subtle) {
    // HTTPS 环境遗留的密文无法在普通 HTTP 下解密，等待会话接口重新填充内存。
    storeProfileValue(SESSION_PROFILE_KEY, null);
    return null;
  }

  const revision = profileRevision;
  try {
    const payload = JSON.parse(cached);
    const bytes = await subtle.decrypt(
      { name: "AES-GCM", iv: fromBase64(payload.iv) },
      await getCryptoKey(),
      fromBase64(payload.data)
    );
    if (revision !== profileRevision) return memoryProfile ? { ...memoryProfile } : null;
    memoryProfile = JSON.parse(new TextDecoder().decode(bytes));
    return { ...memoryProfile };
  } catch (error) {
    if (revision !== profileRevision) return memoryProfile ? { ...memoryProfile } : null;
    clearSessionProfile();
    return null;
  }
}

export function clearSessionProfile({ notify = true, preserveNavigationTarget = false } = {}) {
  profileRevision += 1;
  memoryProfile = null;
  cryptoKeyPromise = undefined;
  if (typeof window !== "undefined") clearRootNavigation(window, { preservePending: preserveNavigationTarget });
  clearStoredSessionProfile({
    storage: getSessionStorage(),
    eventTarget: typeof window !== "undefined" ? window : null,
    notify
  });
}
